import React, { useState, useEffect, useRef } from 'react';
import { FaEnvelope, FaPaperPlane, FaUser, FaSearch, FaRegClock, FaCheck, FaCheckDouble, FaExclamationCircle } from 'react-icons/fa';

import SEO from '../SEO';
import './Messaging.css';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const Messaging = () => {
  const { 
    conversations, 
    selectedConversation, 
    setSelectedConversation, 
    getConversationMessages, 
    markConversationAsRead, 
    sendMessage,
    loading 
  } = useMessaging();
  
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef(null);
  const [forceUpdate, setForceUpdate] = useState(false);
  
  // Force update after timeout if loading persists
  useEffect(() => {
    let timeoutId;
    if (loading) {
      timeoutId = setTimeout(() => {
        console.log("Forcing update after timeout");
        setForceUpdate(prev => !prev);
      }, 6000);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading]);
  
  // Enhanced Debug useEffect
  useEffect(() => {
    console.log('==== MESSAGING COMPONENT DEBUG ====');
    console.log('Current User:', currentUser);
    console.log('Conversations:', conversations);
    console.log('Loading state:', loading);
    console.log('Selected Conversation:', selectedConversation);
    console.log('Authentication state:', !!currentUser);
    console.log('================================');
  }, [currentUser, conversations, loading, selectedConversation, forceUpdate]);
  
  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conversation => 
    conversation.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Get messages for selected conversation
  const conversationMessages = selectedConversation 
    ? getConversationMessages(selectedConversation.id) 
    : [];
  
  // Scroll to bottom of messages when messages update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationMessages]);
  
  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversation) {
      markConversationAsRead(selectedConversation.id);
    }
  }, [selectedConversation, markConversationAsRead]);
  
  // Handle sending a message
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (messageInput.trim() && selectedConversation) {
      sendMessage(selectedConversation.id, messageInput);
      setMessageInput('');
    }
  };
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = typeof timestamp === 'object' ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  return (
    <>
      <SEO 
        title="Messaging - Student Helper"
        description="Connect with other students and experts through our messaging system."
        keywords="student messaging, academic communication, student chat"
      />
      <div className="messaging">
        <div className="header">
          <h1>Messaging</h1>
          <p>Connect with admin and staff</p>
        </div>

        <div className="messaging-container">
          <div className="conversations-list">
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search conversations..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading conversations...</p>
                {/* Debug info when loading */}
                {process.env.NODE_ENV !== 'production' && (
                  <div className="debug-info">
                    <p>Auth Status: {currentUser ? 'Logged In' : 'Not Logged In'}</p>
                    <p>User ID: {currentUser?.uid || 'No User'}</p>
                    <p>Loading: {loading ? 'True' : 'False'}</p>
                  </div>
                )}
              </div>
            ) : filteredConversations.length > 0 ? (
              <div className="conversations">
                {filteredConversations.map(conversation => (
                  <div 
                    key={conversation.id}
                    className={`conversation-item ${selectedConversation?.id === conversation.id ? 'active' : ''} ${conversation.unread ? 'unread' : ''}`}
                    onClick={() => setSelectedConversation(conversation)}
                  >
                    <div className="conversation-avatar">
                      <FaUser />
                      {conversation.unread && <span className="unread-indicator"></span>}
                    </div>
                    <div className="conversation-content">
                      <div className="conversation-header">
                        <h3>{conversation.displayName}</h3>
                        <span className="conversation-time">{formatTimestamp(conversation.timestamp)}</span>
                      </div>
                      <p className="conversation-preview">{conversation.lastMessage}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-conversations">
                <FaEnvelope className="no-messages-icon" />
                <h2>No Messages</h2>
                <p>Your messages from admin will appear here</p>
                {/* Debug button to create test admin message */}
                {process.env.NODE_ENV !== 'production' && (
                  <div className="debug-section">
                    <p>Debug Info:</p>
                    <p>Auth Status: {currentUser ? 'Logged In' : 'Not Logged In'}</p>
                    <p>User ID: {currentUser?.uid || 'No User'}</p>
                    <button 
                      className="debug-button"
                      onClick={async () => {
                        try {
                          const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
                          const { db } = await import('../../firebase');
                          
                          // Add a test admin message
                          await addDoc(collection(db, 'messages'), {
                            senderId: 'admin123',
                            senderName: 'System Admin',
                            recipientId: currentUser?.uid || 'anonymous',
                            content: 'This is a test admin message. Welcome to the messaging system!',
                            timestamp: serverTimestamp(),
                            read: false,
                            isAdminMessage: true
                          });
                          
                          console.log('Created test admin message');
                        } catch (error) {
                          console.error('Failed to create test message:', error);
                        }
                      }}
                    >
                      Create Test Admin Message
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="chat-area">
            {selectedConversation ? (
              <>
                <div className="chat-header">
                  <FaUser className="user-icon" />
                  <div className="user-info">
                    <h3>{selectedConversation.displayName}</h3>
                    <p>{selectedConversation.isAdmin ? 'Admin' : 'User'}</p>
                  </div>
                </div>
                
                <div className="messages-container">
                  {conversationMessages.length > 0 ? (
                    <div className="messages">
                      {conversationMessages.map(message => (
                        <div 
                          key={message.id}
                          className={`message ${message.senderId === currentUser?.uid ? 'sent' : 'received'}`}
                        >
                          <div className="message-content">
                            <p>{message.content}</p>
                            <div className="message-meta">
                              <span className="message-time">{formatTimestamp(message.timestamp)}</span>
                              {message.senderId === currentUser?.uid && (
                                <span className="message-status">
                                  {message.read ? <FaCheckDouble /> : <FaCheck />}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="no-messages">
                      <FaPaperPlane className="send-icon" />
                      <p>No messages in this conversation</p>
                      <p>Send a message to start the conversation</p>
                    </div>
                  )}
                </div>
                
                <form className="message-input" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    placeholder="Type your message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                  />
                  <button type="submit" disabled={!messageInput.trim()}>
                    <FaPaperPlane />
                  </button>
                </form>
              </>
            ) : (
              <div className="chat-placeholder">
                <div className="no-chat-selected">
                  <FaEnvelope className="placeholder-icon" />
                  <h3>Select a conversation</h3>
                  <p>Choose a conversation to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Messaging; 